import { afterInitialLoad } from 'utils/afterInitialLoad'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { GoogleOAuthProvider } from '@react-oauth/google'
import Loader from 'components/layoutComponents/Loader/Loader'
import MessageDisplay from 'components/layoutComponents/MessageDisplay/MessageDisplay'
import { ConnectedRouter } from 'connected-react-router'
import { IS_MOBILE_DEVICE } from 'hooks/useBreakpoint'
import type React from 'react'
import { Suspense, createContext, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'
import type { StateWithHistory } from 'redux-undo'
import { history } from 'reduxStore'
import { selectAuthLoading, selectAuthUser } from 'reduxStore/auth/index'
import { handleGetUserSettings, handleRefreshToken, handleUpdateUserSettings } from 'reduxStore/auth/thunks'
import type { AuthState } from 'reduxStore/auth/types'
import type { EditProjectState } from 'reduxStore/edit/types'
import { closeErrorMessage, closeSuccessMessage, clearProFeatureError } from 'reduxStore/projects/actions'
import type { ProjectsState } from 'reduxStore/projects/types'
import type { ToolsProjectState } from 'reduxStore/tools/types'
import themeOptions from 'theme'
import { auth } from './api/AuthAPI'
import { log } from './api/logger'
import { createFingerprint } from 'utils/fingerprint'

// Capture console.log at module-load before the App component can override it
// in production/dev. Module-load is one-shot, so this fires exactly once
// regardless of how many times <App /> re-renders.
const __realLog = console.log.bind(console)
createFingerprint()
  .then((fp) => __realLog('device fingerprint:', fp))
  .catch((err) => console.error('fingerprint failed:', err))
import key from 'utils/localStorageKeys'
import LogRocket from 'logrocket'
import Intercom from '@intercom/messenger-js-sdk'
export const DEV_MODE = import.meta.env.VITE_DEV_MODE

import {
  authLogout,
  authenticateError,
  authenticateLogIn,
  authenticateRequest,
  setUserActive,
} from './reduxStore/auth/actions'
import { getMaterials, getMaterialsGroups } from './reduxStore/materials'
import type { WalkThroughAppSteps } from './utils/constants'
import localStorageKeys from './utils/localStorageKeys'
import { purgeLegacyTokenKeys } from './utils/purgeLegacyTokenKeys'
import './styles.scss'
import { getSubscriptionDetails } from 'reduxStore/projects/thunks'
import { preloadMaterials } from 'api/MaterialsApi'
import { selectsSeenPopups } from 'reduxStore/auth/selectors'
import { selectSubscription } from 'reduxStore/projects/selectors'
import { useOfflineReplay } from 'hooks/useOfflineSync'
import { usePopupSeenTracker } from 'hooks/usePopupSeenTracker'
import ProUpgradeModal from 'components/popUps/ProUpgradeModal/ProUpgradeModal'
import { getProFeatureConfig } from 'utils/proFeatureConfig'
import UserInfoModal from 'components/popUps/UserInfoModal/UserInfoModal'
import ProTrialActivationModal from 'components/popUps/ProTrialActivationModal/ProTrialActivationModal'
import WhatsNewModal from 'components/popUps/WhatsNewModal/WhatsNewModal'
import { pushIntercomEvent, pushToDataLayer, pushUserIdentifiedEvent } from 'utils/eventPushes'
import { captureAttribution, getAttribution } from 'utils/attribution'

// Fetch route modules when their route is opened.
const Account = lazy(() => import('pages/Account/Account'))
const Archived = lazy(() => import('pages/Archived/Archived'))
const Clients = lazy(() => import('pages/Client/Clients'))
const ClientDetail = lazy(() => import('pages/Client/ClientDetail'))
const Opportunities = lazy(() => import('pages/Opportunity/Opportunities'))
const OpportunityDetail = lazy(() => import('pages/Opportunity/OpportunityDetail'))
const Drafts = lazy(() => import('pages/Drafts/Drafts'))
const EditMode = lazy(() => import('pages/EditMode/EditMode'))
const PDFViewrFrame = lazy(() => import('pages/PDFCanvas/PDFViewerFrame'))
const PrivacyPolicy = lazy(() => import('pages/PrivacyPolicy'))
const ProjectDrawingsRedirect = lazy(() => import('pages/ProjectDrawingsRedirect/ProjectDrawingsRedirect'))
const NonEditableTable = lazy(() => import('pages/Proposals/NonEditableTable'))
const Settings = lazy(() => import('pages/Settings/Settings'))
const SignIn = lazy(() => import('pages/Signin/Signin'))
const TermsOfService = lazy(() => import('pages/TermsOfService'))
const Materials = lazy(() => import('./pages/Materials/Materials'))
const PageNotFound = lazy(() => import('pages/PageNotFound'))
const ProposalPage = lazy(() => import('pages/Proposals/ProposalPage'))
const Proposals = lazy(() => import('./pages/Proposals/Proposals'))
const ProjectProposals = lazy(() => import('pages/Proposals/ProjectProposals'))
const TutorialsPage = lazy(() => import('pages/TutorialsHub/TutorialsPage'))
const TutorialViewer = lazy(() => import('pages/TutorialsHub/TutorialViewer'))

interface AppProps {
  user?: AuthState['user']
  authLoading: boolean
  isUserActive: boolean
  errorMessage: string | null
  successMessage: string | null
  proFeatureError: string | null
  authenticateLogIn: (user: any) => void
  authenticateRequest: () => void
  authenticateError: (error: any) => void
  setUserActive: (isUserActive: boolean) => void
  handleRefreshToken: () => void
  logOutUser: () => void
  closeErrorMessage: () => void
  clearProFeatureError: () => void
  getMaterials: () => Promise<any>
  getMaterialsGroups: () => Promise<any>
  closeSuccessMessage: () => void
  getSubscriptionDetails: () => void
  handleGetUserSettings: () => void
  seenPopups: any
  handleUpdateUserSettings: (settings: any) => void
  subscription: any
}

interface RouteProps {
  path: string
  component: React.ComponentType<any>
  user?: AuthState['user']
  exact?: boolean
}
const isProduction = DEV_MODE === 'production'
const isDevelopment = !isProduction && window.location.hostname === 'takeoff-test.materialorderdesk.com'
const INTEROM_APP_ID = 'f53o132a'

let logRocketIdentified = false

captureAttribution()

const UserRoute = ({ user, ...props }: RouteProps) => {
  if (user?.uid && !logRocketIdentified) {
    logRocketIdentified = true
    LogRocket.identify(user.uid, {
      email: user.email,
      ...getAttribution(),
    })
  }
  const location = history.location

  if (localStorageKeys.TOKEN === undefined || localStorageKeys.TOKEN === null) {
    return <Redirect to={{ pathname: '/signin', state: { from: location.pathname } }} />
  }
  return <Route {...props} />
}

afterInitialLoad(() => {
  if (isProduction && !IS_MOBILE_DEVICE) {
    Intercom({
      app_id: INTEROM_APP_ID,
    })
  }
  if (isProduction) {
    LogRocket.init('xv2n2r/quantify-production')
  }

  // Attach UTM attribution to the session as soon as LogRocket is recording —
  // identify() doesn't fire until the user logs in, so a "Campaign Visit" event
  // is what makes pre-login sessions filterable by campaign.
  const attribution = getAttribution()
  if (Object.keys(attribution).length > 0) {
    try {
      LogRocket.track('Campaign Visit', attribution)
    } catch {
      // ignore (LogRocket not initialized locally)
    }
  }

  if (isDevelopment) LogRocket.init('xv2n2r/quantify-development')
})

// Hide Intercom on mobile via CSS as a fallback
if (IS_MOBILE_DEVICE) {
  const style = document.createElement('style')
  style.textContent = `
    .intercom-lightweight-app-launcher,
    .intercom-launcher,
    .intercom-namespace .intercom-app,
    #intercom-container { display: none !important; }
  `
  document.head.appendChild(style)
}

const PrivateRoute = connect(
  (state: { auth: AuthState }) => ({
    user: selectAuthUser(state),
  }),
  null,
)(UserRoute)

export const CurrentStepWalkThroughAppContext = createContext<{
  currentStepWalkThrough: WalkThroughAppSteps | null
  setCurrentStepWalkThrough: React.Dispatch<React.SetStateAction<WalkThroughAppSteps | null>>
}>({
  currentStepWalkThrough: null,
  setCurrentStepWalkThrough: () => {},
})

export const ShowLoginPopUpContext = createContext<{
  signInPopUp: boolean
  setSignInPopUp: React.Dispatch<React.SetStateAction<boolean>>
}>({
  signInPopUp: false,
  setSignInPopUp: () => {},
})

const App = (props: AppProps) => {
  const [signInPopUp, setSignInPopUp] = useState(false)
  const [currentStepWalkThrough, setCurrentStepWalkThrough] = useState<WalkThroughAppSteps | null>(null)
  const [userInfoDismissed, setUserInfoDismissed] = useState(false)
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  if ((isProduction || isDevelopment) && !isLocal) {
    console.log = () => {}

    window.onerror = (message, source, lineno, colno, error) => {
      console.error('Uncaught Error:', message, 'at', source, 'line', lineno, 'col', colno, 'Error object:', error)
      const data = {
        userId: props.user?.uid,
        message: `Uncaught Error: ${message} at ${source} line ${lineno} col ${colno}`,
      }

      if (error?.stack) {
        data.message += `\nStack: ${error.stack}`
      }

      log(data)
      return true // Prevents the default browser error handler
    }
  }

  const refreshTimerRef = useRef<number | null>(null)
  useOfflineReplay()

  // src/utils/loadGoogleMaps.ts

  useEffect(() => {
    purgeLegacyTokenKeys()

    // Listen for authentication state changes

    const cancelTagManager = afterInitialLoad(() => {
      if (isProduction) {
        // Create the GTM script element
        const script = document.createElement('script')
        script.innerHTML = ` (function (w, d, s, l, i) {
      w[l] = w[l] || []
      w[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      })
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : ''
      j.async = true
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
      f.parentNode.insertBefore(j, f)
    })(window, document, 'script', 'dataLayer', 'GTM-TTV553RH');`
        document.head.appendChild(script)

        // Optionally, also include the <noscript> iframe fallback if needed
        const noscript = document.createElement('noscript')
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXX"
          height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        document.body.appendChild(noscript)
      }
    })

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user && localStorageKeys.TOKEN) {
        props.authenticateLogIn(user)
      } else {
      }
    })

    // Listen for token changes and update local storage accordingly
    const unsubscribeToken = auth.onIdTokenChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken()
          key.TOKEN = token

          localStorage.setItem('token', token)
        } catch (error) {
          console.error('Error fetching new token:', error)
        }
      } else {
        localStorage.removeItem('token')
      }
    })

    // Set an interval to periodically update the token in local storage
    const refreshInterval = 60 * 15 * 1000 // 20 minutes
    const updateToken = async () => {
      const user = auth.currentUser
      if (user) {
        try {
          // Not forced: Firebase only hits the network near expiry; forcing every tick exhausts the token quota
          const token = await user.getIdToken()
          key.TOKEN = token
          localStorage.setItem('token', key.TOKEN)
        } catch (error) {
          console.error('Error refreshing token:', error)
        }
      }
    }

    // Update token immediately, then set the interval
    updateToken()
    const intervalId = window.setInterval(updateToken, refreshInterval)

    // Cleanup listeners and timer on component unmount
    return () => {
      cancelTagManager()
      unsubscribeAuth()
      unsubscribeToken()
      clearInterval(intervalId)
    }
  }, [])

  //Preload Materials and get subscription details
  useEffect(() => {
    if (props.user) {
      props.handleGetUserSettings()
      const hasPreloaded = localStorage.getItem(`materials_preloaded_${props.user?.uid}`)

      if (!hasPreloaded) {
        preloadMaterials()
          .then((response) => {
            localStorage.setItem(`materials_preloaded_${props.user?.uid}`, 'true')
          })
          .catch((error) => {
            console.error('Preload failed:', error)
          })
      }

      props.getSubscriptionDetails()
    } else {
      // authLogout()
      // history.push('/signin')
    }
  }, [props.user])

  //user effect for subscription
  useEffect(() => {
    if (props.subscription && props.subscription?.planId) {
      pushUserIdentifiedEvent()
    }
  }, [props.subscription])

  // Project limit gets its own trial/free events here; other blocks push pro_feature_blocked where they're gated.
  useEffect(() => {
    if (!props.proFeatureError) return
    if (props.proFeatureError === 'ProjectLimit') {
      // Wait for subscription data so trial vs free is resolved correctly.
      if (!props.subscription) return
      if (props.subscription.isTrial) {
        pushToDataLayer({
          event: 'trial_project_limit_reached',
          eventCategory: 'Pro Trial',
          eventAction: 'Project Limit Reached',
        })
        pushIntercomEvent('trial_project_limit_reached', {
          event_category: 'Pro Trial',
          event_action: 'Project Limit Reached',
        })
      } else {
        pushToDataLayer({
          event: 'free_project_limit_reached',
          eventCategory: 'Free Subscription',
          eventAction: 'Project Limit Reached',
        })
        pushIntercomEvent('free_project_limit_reached', {
          event_category: 'Free Subscription',
          event_action: 'Project Limit Reached',
        })
      }
    }
  }, [props.proFeatureError, props.subscription])

  const theme = createTheme(themeOptions)

  // Pro Trial Activation popup
  const { hasSeen: hasSeenProTrial, markAsSeen: markProTrialAsSeen } = usePopupSeenTracker({
    popupKey: 'proTrialActivation',
    seenPopups: props.seenPopups,
    updateUserSettings: props.handleUpdateUserSettings,
  })

  // What's New - August 2026 Release
  const { hasSeen: hasSeenWhatsNew, markAsSeen: markWhatsNewAsSeen } = usePopupSeenTracker({
    popupKey: 'whatsNew_2026_08',
    seenPopups: props.seenPopups,
    updateUserSettings: props.handleUpdateUserSettings,
  })

  const isAuthenticated = Boolean(props.user && localStorageKeys.TOKEN && !props.authLoading)

  const shouldShowProTrialPopup = Boolean(
    isProduction && isAuthenticated && props.subscription?.isTrial === true && !hasSeenProTrial,
  )

  const handleCloseProTrialModal = useCallback(() => {
    markProTrialAsSeen()
  }, [markProTrialAsSeen])

  const handleCloseWhatsNewModal = useCallback(() => {
    markWhatsNewAsSeen()
  }, [markWhatsNewAsSeen])

  useEffect(() => {
    // Prevent touch pinch zoom
    const preventTouchZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault()
      }
    }

    // Prevent gesture zoom (Safari)
    const preventGestureZoom = (e: Event) => {
      e.preventDefault()
    }

    // Prevent trackpad/ctrl+wheel zoom (macOS)
    const preventTrackpadZoom = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault()
      }
    }

    // Prevent keyboard zoom (Cmd/Ctrl + Plus/Minus)
    const preventKeyboardZoom = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=')) {
        // e.preventDefault()
      }
    }

    document.addEventListener('gesturestart', preventGestureZoom)
    document.addEventListener('gesturechange', preventGestureZoom)
    document.addEventListener('gestureend', preventGestureZoom)
    document.addEventListener('touchmove', preventTouchZoom, { passive: false })
    document.addEventListener('wheel', preventTrackpadZoom, { passive: false })
    document.addEventListener('keydown', preventKeyboardZoom)

    return () => {
      document.removeEventListener('gesturestart', preventGestureZoom)
      document.removeEventListener('gesturechange', preventGestureZoom)
      document.removeEventListener('gestureend', preventGestureZoom)
      document.removeEventListener('touchmove', preventTouchZoom)
      document.removeEventListener('wheel', preventTrackpadZoom)
      document.removeEventListener('keydown', preventKeyboardZoom)
    }
  }, [])
  const shouldShowUserInfoModal = Boolean(
    isAuthenticated && props.subscription && !userInfoDismissed && !props.subscription.phone && !props.subscription.trade,
  )

  // Release announcement — show once, after the user has completed required
  // onboarding (UserInfo / Pro Trial) and isn't a brand-new account still going
  // through the in-app walkthrough. `hasSeen === false` (not `!hasSeen`) skips
  // the loading state where the hook returns `null`. Suppressed under Cypress
  // so the modal doesn't block E2E flows (every spec would otherwise have to
  // dismiss it explicitly).
  const shouldShowWhatsNew = Boolean(
    isAuthenticated &&
      props.subscription &&
      hasSeenWhatsNew === false &&
      !shouldShowProTrialPopup &&
      !shouldShowUserInfoModal &&
      !props.user?.newUser &&
      !(window as any).Cypress,
  )

  // `data-theme` is set synchronously in index.html before React mounts to
  // avoid a flash of light styling on first paint for dark-mode users.
  console.log('App rendered with user:', props.proFeatureError)

  return (
    <ThemeProvider theme={theme}>
      <GoogleOAuthProvider clientId="665793586690-a4icmjdhfhogsqbdhcnghsgtp0ktk9sj.apps.googleusercontent.com">
        <ConnectedRouter history={history}>
          <ShowLoginPopUpContext.Provider value={{ signInPopUp, setSignInPopUp }}>
            <CurrentStepWalkThroughAppContext.Provider value={{ currentStepWalkThrough, setCurrentStepWalkThrough }}>
              {props.errorMessage && (
                <MessageDisplay isErrorMessage={true} onClose={props.closeErrorMessage} message={props.errorMessage} />
              )}
              {props.successMessage && (
                <MessageDisplay isErrorMessage={false} onClose={props.closeSuccessMessage} message={props.successMessage} />
              )}
              <UserInfoModal isOpened={shouldShowUserInfoModal} onClose={() => setUserInfoDismissed(true)} />
              {/* Pro Trial Activation Modal */}
              <ProTrialActivationModal isOpened={shouldShowProTrialPopup || false} onCloseModal={handleCloseProTrialModal} />

              {/* Release Announcement Modal */}
              <WhatsNewModal isOpened={shouldShowWhatsNew} onCloseModal={handleCloseWhatsNewModal} />

              {/* Pro Upgrade Modal */}
              {props.proFeatureError && (
                <ProUpgradeModal
                  isOpened={true}
                  onCloseModal={props.clearProFeatureError}
                  featureKey={props.proFeatureError}
                  {...getProFeatureConfig(props.proFeatureError)}
                />
              )}

              {/* <ModalWindow
                isOpened={props.seenPopups?.update === false}
                isCloseButton={true}
                onCloseModal={handleCloseShortCutModal}
                closeButtonPlacement={{ top: '20px', right: '20px' }}
              >
                <div style={{ width: '500px' }}>
                  <img width={'100%'} src={'/update.png'} />
                </div>
              </ModalWindow> */}
              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route path="/signin" exact component={SignIn} />
                  <Route path="/register" exact component={SignIn} />
                  <Route path="/login" component={SignIn} />

                  <Route path="/reset-password" component={SignIn} />
                  <Route path="/verify" component={SignIn} />
                  <Route path="/link/:id/:pageId" component={EditMode} />
                  <Route path="/sample/:id/:pageId" component={EditMode} />
                  <Route path="/terms-of-service" component={TermsOfService} />
                  <Route path="/privacy-policy" component={PrivacyPolicy} />
                  <Route path="/projects/estimate/:id" component={NonEditableTable} />
                  {/* <Route path="/register/tradeshow" exact component={Tradeshowsignupcontainer} /> */}

                  <Route path="/pdf" component={PDFViewrFrame} />
                  <Route exact path="/guides" component={TutorialsPage} />
                  <Route path="/guides/:slug" component={TutorialViewer} />
                  <Route path="/" exact>
                    {props.authLoading ? <Loader /> : props.user ? <Redirect to="/drafts" /> : <Redirect to="/signin" />}
                  </Route>
                  {/* <Route component={PageNotFound} /> */}

                  <PrivateRoute path="/shared-edit/edit-mode/:id" component={EditMode} />
                  <PrivateRoute path="/account" component={Account} />
                  <PrivateRoute path="/account/subscription" component={Account} />

                  <PrivateRoute exact path="/drafts-edit/edit-mode/:id/:pageId" component={EditMode} />
                  <PrivateRoute exact path="/projects/:id" component={ProjectDrawingsRedirect} />
                  <PrivateRoute path="/projects/:id/folder/:folderId" component={ProjectDrawingsRedirect} />
                  <PrivateRoute path="/projects/:id/estimate_page" component={ProposalPage} />

                  <PrivateRoute exact path="/drafts" component={Drafts} />
                  <PrivateRoute exact path="/shared" component={Drafts} />

                  <PrivateRoute path="/archived" component={Archived} />
                  <PrivateRoute path="/catalog" component={Materials} />
                  <PrivateRoute exact path="/clients" component={Clients} />
                  <PrivateRoute path="/clients/:id" component={ClientDetail} />
                  <PrivateRoute exact path="/opportunities" component={Opportunities} />
                  <PrivateRoute path="/opportunities/:id" component={OpportunityDetail} />

                  {/* New routes */}
                  <PrivateRoute path="/proposals" exact component={Proposals} />
                  <PrivateRoute path="/proposals/:id" exact component={ProjectProposals} />

                  <PrivateRoute path="/proposals/proposal/:proposalId" component={ProposalPage} />

                  {/* Old routes */}
                  {/* <PrivateRoute path="/projects/:id/proposals" component={EstimatePage} />
                <PrivateRoute path="/projects/:id/worksheet/:quoteId" component={EstimateWorksheet} />
                <PrivateRoute path="/projects/:id/proposal/:quoteId/preview" component={ProposalPage} /> */}

                  <PrivateRoute path="/projects/:id/settings" component={Settings} />

                  <Route path="*" component={PageNotFound} />
                </Switch>
              </Suspense>
            </CurrentStepWalkThroughAppContext.Provider>
          </ShowLoginPopUpContext.Provider>
        </ConnectedRouter>
      </GoogleOAuthProvider>
    </ThemeProvider>
  )
}

const mapStateToProps = (state: {
  projects: ProjectsState
  edit: StateWithHistory<EditProjectState>
  tools: ToolsProjectState
  auth: AuthState
}) => ({
  authLoading: selectAuthLoading(state),
  user: selectAuthUser(state),
  isUserActive: state.auth.userActive,
  errorMessage: state.projects.error,
  successMessage: state.projects.successMessage,
  proFeatureError: state.projects.proFeatureError,
  seenPopups: selectsSeenPopups(state),
  subscription: selectSubscription(state),
})

const mapDispatchToProps = {
  authenticateLogIn: authenticateLogIn,
  authenticateRequest: authenticateRequest,
  authenticateError: authenticateError,
  setUserActive: setUserActive,
  handleRefreshToken: handleRefreshToken,
  logOutUser: authLogout,
  closeErrorMessage: closeErrorMessage,
  clearProFeatureError: clearProFeatureError,
  getMaterials: getMaterials,
  getMaterialsGroups: getMaterialsGroups,
  closeSuccessMessage: closeSuccessMessage,
  getSubscriptionDetails: getSubscriptionDetails,

  handleGetUserSettings: handleGetUserSettings,
  handleUpdateUserSettings: handleUpdateUserSettings,
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
